<template>
    <section
        class="install-promotion"
        :class="[
            `install-promotion--${previewSize}`,
            {'install-promotion--success': success}
        ]"
        aria-live="polite"
    >
        <InstallAppPreview
            v-if="showPreview"
            :size="previewSize"
            class="install-promotion__preview"
        />

        <div class="install-promotion__body">
            <div class="install-promotion__eyebrow">
                {{ success ? 'ДЕЙСТВИЕ ВЫПОЛНЕНО' : 'АРГУС НА ЭТОМ УСТРОЙСТВЕ' }}
            </div>
            <h2>{{ title }}</h2>
            <p>{{ description }}</p>
            <p v-if="isIOS" class="install-promotion__ios-note">
                iPhone не сообщает сайту, появился ли значок. Если Аргус уже есть
                на экране «Домой» этого iPhone, отметьте это вручную.
            </p>
        </div>

        <div class="install-promotion__actions">
            <button
                type="button"
                class="install-promotion__button install-promotion__button--primary"
                :disabled="busy"
                @click="$emit('install')"
            >
                <i class="fas fa-download" aria-hidden="true"></i>
                {{ busy ? 'Готовим безопасный вход…' : ctaLabel }}
            </button>
            <button
                v-if="isIOS"
                type="button"
                class="install-promotion__button install-promotion__button--secondary"
                :disabled="busy"
                @click="$emit('installed')"
            >
                Уже установлено на этом устройстве
            </button>
            <button
                type="button"
                class="install-promotion__button install-promotion__button--secondary"
                :disabled="busy"
                @click="$emit('snooze')"
            >
                Не сейчас
            </button>
            <button
                v-if="showDone"
                type="button"
                class="install-promotion__button install-promotion__button--quiet"
                :disabled="busy"
                @click="$emit('done')"
            >
                Готово, закрыть
            </button>
        </div>
    </section>
</template>

<script>
import InstallAppPreview from '@/components/InstallAppPreview.vue';
import {
    getInstallCtaLabel,
    isIOSInstallDevice
} from '@/utils/appInstallation';

export default {
    name: 'InstallPromotionCard',
    components: {
        InstallAppPreview
    },
    props: {
        success: {
            type: Boolean,
            default: false
        },
        showDone: {
            type: Boolean,
            default: false
        },
        showPreview: {
            type: Boolean,
            default: true
        },
        previewSize: {
            type: String,
            default: 'compact'
        },
        busy: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        isIOS() {
            return isIOSInstallDevice();
        },
        ctaLabel() {
            return getInstallCtaLabel();
        },
        title() {
            return this.success
                ? 'Готово. Добавить Аргус на устройство?'
                : 'Аргус всегда под рукой';
        },
        description() {
            if (this.success) {
                return 'Следующий раз откройте рабочую систему прямо с экрана телефона или рабочего стола.';
            }
            return 'Установите приложение один раз — оно откроется отдельным окном и всегда будет рядом.';
        }
    }
};
</script>

<style scoped>
.install-promotion {
    display: grid;
    grid-template-columns: minmax(150px, 230px) minmax(0, 1fr) minmax(230px, auto);
    align-items: center;
    gap: 18px;
    padding: 18px;
    overflow: hidden;
    border: 1px solid rgba(126, 181, 250, .42);
    border-radius: 20px;
    background:
        radial-gradient(circle at 94% 0, rgba(90, 168, 255, .38), transparent 36%),
        linear-gradient(125deg, #102d58 0%, #164d96 100%);
    color: #fff;
    box-shadow: 0 15px 38px rgba(19, 64, 128, .20);
}

.install-promotion--mini {
    grid-template-columns: 108px minmax(0, 1fr) minmax(220px, auto);
    padding: 14px;
    border-radius: 17px;
}

.install-promotion--success {
    border-color: rgba(119, 206, 168, .48);
    background:
        radial-gradient(circle at 94% 0, rgba(89, 206, 164, .28), transparent 36%),
        linear-gradient(125deg, #113557 0%, #13517d 100%);
}

.install-promotion__preview {
    min-width: 0;
}

.install-promotion__body {
    min-width: 0;
}

.install-promotion__eyebrow {
    margin-bottom: 4px;
    color: #bcd9ff;
    font-size: 10px;
    line-height: 1.3;
    letter-spacing: .12em;
    font-weight: 800;
}

.install-promotion h2 {
    margin: 0 0 5px;
    font-size: 21px;
    line-height: 1.22;
    font-weight: 800;
}

.install-promotion p {
    max-width: 620px;
    margin: 0;
    color: #d9e9ff;
    font-size: 14px;
    line-height: 1.45;
}

.install-promotion__ios-note {
    margin-top: 8px !important;
    padding: 8px 10px;
    border-radius: 10px;
    background: rgba(255, 255, 255, .09);
    color: #fff !important;
    font-size: 12px !important;
}

.install-promotion__actions {
    display: grid;
    min-width: 230px;
    gap: 7px;
}

.install-promotion__button {
    min-height: 38px;
    padding: 8px 13px;
    border: 0;
    border-radius: 11px;
    font: inherit;
    font-size: 13px;
    font-weight: 800;
    cursor: pointer;
}

.install-promotion__button:disabled {
    cursor: wait;
    opacity: .68;
}

.install-promotion__button:focus-visible {
    outline: 3px solid rgba(255, 255, 255, .54);
    outline-offset: 2px;
}

.install-promotion__button--primary {
    background: #fff;
    color: #12478c;
    box-shadow: 0 8px 20px rgba(6, 30, 67, .22);
}

.install-promotion__button--primary i {
    margin-right: 6px;
}

.install-promotion__button--secondary,
.install-promotion__button--quiet {
    background: rgba(255, 255, 255, .11);
    color: #edf6ff;
}

.install-promotion__button--quiet {
    background: transparent;
    color: #c9ddf7;
}

@media (max-width: 900px) {
    .install-promotion {
        grid-template-columns: 135px minmax(0, 1fr);
    }

    .install-promotion__actions {
        grid-column: 1 / -1;
        min-width: 0;
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .install-promotion__button--primary {
        grid-column: 1 / -1;
    }
}

@media (max-width: 575.98px) {
    .install-promotion,
    .install-promotion--mini {
        grid-template-columns: 104px minmax(0, 1fr);
        align-items: start;
        gap: 12px;
        padding: 14px;
        border-radius: 17px;
    }

    .install-promotion h2 {
        font-size: 18px;
    }

    .install-promotion p {
        font-size: 13px;
    }

    .install-promotion__actions {
        grid-template-columns: 1fr;
    }

    .install-promotion__button {
        width: 100%;
        min-height: 43px;
        white-space: normal;
    }

    .install-promotion__button--primary {
        grid-column: auto;
    }
}
</style>
